import axios, { AxiosInstance } from "axios";
import { makeAuthorizationHeader } from "./utils";
import defaultAxios from "./axios";
import MainStore from "@/store";
import { TokenService } from "./storage";
import MainRouter from "@/router";

const { API_URL } = defaultAxios;

export let api: AxiosInstance;

// Configurações do sistema de refresh cross-aba
const REFRESH_LOCK_KEY = "auth:refresh-lock";
const REFRESH_LOCK_TTL = 30000; // 30 segundos
const TOKEN_UPDATED_KEY = "auth:token-updated";
const BROADCAST_CHANNEL_NAME = "auth-refresh";

// Estado global do refresh
let refreshingPromise: Promise<string> | null = null;
let pendingRequests: Array<{
  resolve: (value: any) => void;
  reject: (reason: any) => void;
  config: any;
}> = [];

// Canal de comunicação entre abas (com fallback)
let broadcastChannel: BroadcastChannel | null = null;
let isListeningToStorage = false;

// Gerenciar lock no localStorage com TTL
const acquireLock = (): boolean => {
  const now = Date.now();
  const existingLock = localStorage.getItem(REFRESH_LOCK_KEY);

  if (existingLock) {
    const lockTime = parseInt(existingLock, 10);
    // Se o lock ainda é válido (não expirou)
    if (now - lockTime < REFRESH_LOCK_TTL) {
      return false; // Não conseguiu o lock
    }
  }

  // Adquirir o lock
  localStorage.setItem(REFRESH_LOCK_KEY, now.toString());
  return true;
};

const releaseLock = (): void => {
  localStorage.removeItem(REFRESH_LOCK_KEY);
};

// Propagar token atualizado para outras abas
const propagateTokenUpdate = (token: string): void => {
  // Usar BroadcastChannel se disponível
  if (broadcastChannel) {
    try {
      broadcastChannel.postMessage({ type: "TOKEN_UPDATED", token });
    } catch (error) {
      console.warn("Erro ao enviar via BroadcastChannel:", error);
    }
  }

  // Fallback: usar storage event
  if (!broadcastChannel) {
    localStorage.setItem("auth:token-updated", token);
    // Remover após um pequeno delay para garantir que o evento foi disparado
    setTimeout(() => {
      localStorage.removeItem("auth:token-updated");
    }, 100);
  }
};

// Processar token atualizado de outra aba
const handleTokenUpdated = (token: string): void => {
  if (token && pendingRequests.length > 0) {
    // Reexecutar todas as requisições pendentes com o novo token
    const requests = [...pendingRequests];
    pendingRequests = [];

    requests.forEach(({ resolve, reject, config }) => {
      // Atualizar header com novo token
      const authHeaders = makeAuthorizationHeader(token);
      Object.assign(config.headers, authHeaders);

      // Reexecutar requisição
      api(config).then(resolve).catch(reject);
    });
  }
};

// Adicionar requisição à fila
const addToQueue = (config: any): Promise<any> => {
  return new Promise((resolve, reject) => {
    pendingRequests.push({ resolve, reject, config });
  });
};

// Inicializar comunicação cross-aba
const initializeCrossTabCommunication = (): void => {
  // Tentar usar BroadcastChannel (mais moderno)
  if (typeof BroadcastChannel !== "undefined") {
    try {
      broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      broadcastChannel.onmessage = (event) => {
        if (event.data.type === "TOKEN_UPDATED") {
          handleTokenUpdated(event.data.token);
        }
      };
    } catch (error) {
      console.warn("BroadcastChannel não disponível, usando fallback storage");
      broadcastChannel = null;
    }
  }

  // Fallback: usar eventos de storage
  if (!broadcastChannel && !isListeningToStorage) {
    isListeningToStorage = true;
    window.addEventListener("storage", (event) => {
      if (event.key === TOKEN_UPDATED_KEY && event.newValue) {
        handleTokenUpdated(event.newValue);
        // Limpar imediatamente para não interferir com outras abas
        localStorage.removeItem(TOKEN_UPDATED_KEY);
      }
    });
  }
};

export const initialize = (): void => {
  const instance = axios.create({
    baseURL: API_URL,
    withCredentials: true,
  });

  api = instance;

  // Inicializar comunicação cross-aba
  initializeCrossTabCommunication();

  // Interceptor de REQUEST para sempre usar o token mais atual do localStorage
  api.interceptors.request.use(
    (config) => {
      const currentToken = TokenService.getToken();
      if (currentToken) {
        const authHeaders = makeAuthorizationHeader(currentToken);
        Object.assign(config.headers, authHeaders);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  api.interceptors.response.use(
    (config) => config,
    async (error) => {
      const originalRequest = error.config;

      // Se o erro for 401 (Não autorizado) e não for uma requisição de refresh token
      if (
        error.response?.status === 401 &&
        !originalRequest.url?.includes("/login/refresh-token")
      ) {
        // Garante que headers existe
        originalRequest.headers ??= {};

        // SISTEMA SINGLE-FLIGHT CROSS-ABA

        // Tentar adquirir o lock para ser a aba líder
        const isLeader = acquireLock();

        if (isLeader) {
          // ESTA ABA É A LÍDER - executa o refresh
          if (!refreshingPromise) {
            refreshingPromise = MainStore.dispatch("onboarding/requestNewToken")
              .then(() => {
                const newToken = TokenService.getToken();
                if (newToken) {
                  // Propagar token para outras abas
                  propagateTokenUpdate(newToken);
                }
                return newToken;
              })
              .catch((error) => {
                // Se for erro 401 no refresh token, desloga o usuário
                if (error.response?.status === 401) {
                  MainStore.dispatch("onboarding/requestLogout", false);
                  MainRouter.push({ name: "loginPage" });
                }
                throw error;
              })
              .finally(() => {
                // Liberar o lock e limpar a promise
                releaseLock();
                refreshingPromise = null;
              });
          }

          // A primeira requisição que iniciou o refresh TAMBÉM vai para a fila
          return addToQueue(originalRequest);
        } else {
          // ESTA ABA É SEGUIDORA - aguarda token de outra aba
          return addToQueue(originalRequest);
        }
      } else if (
        error.response?.status === 401 &&
        originalRequest.url?.includes("/login/refresh-token")
      ) {
        // Se falhar ao atualizar o token, desloga o usuário e redireciona para login
        MainStore.dispatch("onboarding/requestLogout", false);
        MainRouter.push({ name: "loginPage" });
      }

      return Promise.reject(error);
    }
  );
};
